import React, { useEffect, useRef, useState } from 'react';

interface Option {
  value: string;
  label: string;
}

interface Props {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
}

const MultiSelectDropdown: React.FC<Props> = ({ options, selected, onChange, placeholder = 'Select...', className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(s => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const display = () => {
    if (selected.length === 0) return placeholder;
    const labels = options.filter(o => selected.includes(o.value)).map(o => o.label);
    return labels.join(', ');
  };

  return (
    <div ref={ref} className={`relative inline-block text-left ${className}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-48 bg-gray-900 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-300 hover:border-gray-500 focus:outline-none"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="truncate">{display()}</span>
        <svg className="ml-2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={open ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
        </svg>
      </button>

      {open && (
        <div className="absolute mt-1 w-48 bg-gray-900 border border-gray-800 rounded-md shadow-lg z-50">
          <div className="p-2 max-h-56 overflow-auto">
            <div className="flex items-center justify-between px-1 mb-1">
              <button onClick={(e) => { e.stopPropagation(); onChange(options.map(o => o.value)); }} className="text-xs text-blue-400 hover:underline">Select all</button>
              <button onClick={clear} className="text-xs text-gray-400 hover:underline">Clear</button>
            </div>
            <div className="space-y-1">
              {options.map(opt => (
                <label key={opt.value} className="flex items-center space-x-2 text-xs text-gray-300 px-1 py-1 hover:bg-gray-800 rounded">
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.value)}
                    onChange={(e) => { e.stopPropagation(); toggleOption(opt.value); }}
                    className="form-checkbox h-4 w-4 text-blue-400 bg-gray-700 border-gray-600"
                  />
                  <span className="truncate">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
